import React, { useEffect, useState } from "react";
import SettingsLayout from "@/components/SettingsLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LocaleCard from "@/components/LocaleCard";
import { Save, Settings as SettingsIcon, Database, Mail, Shield, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function OrgSettings() {
  const { refresh } = useAuth();
  const [org, setOrg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/orgs/me");
        setOrg(data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/orgs/me", {
        name: org.name,
        retention_days: Number(org.retention_days),
        branding: org.branding || {},
        weekly_digest_enabled: !!org.weekly_digest_enabled,
        returning_visitor_enabled: !!org.returning_visitor_enabled,
        returning_visitor_estimated_seconds_saved: Number(org.returning_visitor_estimated_seconds_saved) || 60,
        data_controller_name: (org.data_controller_name || "").trim(),
        data_controller_address: (org.data_controller_address || "").trim(),
      });
      setOrg(data);
      await refresh();
      toast.success("Settings saved");
      // Pre-launch checklist watches this event to refresh the has_data_controller row.
      window.dispatchEvent(new Event("onboarding-status-changed"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const resetDemo = async () => {
    if (!window.confirm("Reset demo data? This wipes sites/hosts/visits created by the seed.")) return;
    try {
      await api.post("/onboarding/reset-sample-data");
      toast.success("Demo data reset");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!org) return <SettingsLayout><div className="p-10 text-navy-500">Loading…</div></SettingsLayout>;

  return (
    <SettingsLayout>
      <div className="max-w-2xl">
        <div className="eb-overline mb-2">Organisation</div>
        <h1 className="text-3xl font-semibold" data-testid="settings-heading">Settings</h1>
        <p className="text-sm text-navy-500 mt-1">Configure your organisation-wide defaults. Customise behaviour and personalised options across the platform from this centre.</p>

        <form onSubmit={save} className="eb-card p-6 mt-8 space-y-4">
          <div>
            <label className="eb-label">Organisation name</label>
            <input value={org.name || ""} onChange={(e) => setOrg({ ...org, name: e.target.value })} className="eb-input" data-testid="org-name-input" />
          </div>
          <div>
            <label className="eb-label">Country</label>
            <input value={org.country || ""} disabled className="eb-input bg-surface text-navy-500" data-testid="org-country-input" />
          </div>
          <div>
            <label className="eb-label">Data retention (days)</label>
            <input type="number" min="1" max="3650" value={org.retention_days ?? 90} onChange={(e) => setOrg({ ...org, retention_days: e.target.value })} className="eb-input max-w-[140px]" data-testid="retention-input" />
            <p className="text-xs text-navy-500 mt-1">Visits and logs beyond this age are permanently deleted (GDPR-compliant).</p>
          </div>
          <div className="border-t border-navy-100 pt-4">
            <div className="eb-overline mb-3 inline-flex items-center gap-2">
              <Shield className="w-3.5 h-3.5 text-accent" />
              Data controller · GDPR
            </div>
            <p className="text-xs text-navy-500 mb-3">
              Required for your <strong>Article 30 Record of Processing</strong> PDF and the
              public privacy notice. Must be populated before you can go live.
            </p>
            <label className="eb-label">Data controller name</label>
            <input
              value={org.data_controller_name || ""}
              onChange={(e) => setOrg({ ...org, data_controller_name: e.target.value })}
              className="eb-input"
              placeholder="e.g. Barking Holdings Ltd"
              maxLength={200}
              data-testid="data-controller-name-input"
            />
            <label className="eb-label mt-4">Data controller address</label>
            <textarea
              value={org.data_controller_address || ""}
              onChange={(e) => setOrg({ ...org, data_controller_address: e.target.value })}
              className="eb-input min-h-[88px] font-normal"
              placeholder={"e.g.\n1 Example Road\nBarking, IG11 8HH\nUnited Kingdom"}
              maxLength={500}
              data-testid="data-controller-address-input"
            />
            <p className="text-xs text-navy-400 mt-1">Max 500 characters.</p>
          </div>

          <div className="border-t border-navy-100 pt-4">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="weekly-digest-toggle-row">
              <input
                type="checkbox"
                checked={org.weekly_digest_enabled !== false}
                onChange={(e) => setOrg({ ...org, weekly_digest_enabled: e.target.checked })}
                className="mt-1 h-4 w-4 accent-accent"
                data-testid="weekly-digest-toggle"
              />
              <span className="flex-1">
                <span className="flex items-center gap-2 eb-label !mb-0">
                  <Mail className="w-4 h-4 text-accent" />
                  Weekly digest email
                </span>
                <span className="block text-xs text-navy-500 mt-1">
                  Mondays at 09:00 UTC, every org admin receives a previous-week PDF summary
                  (visitor counts, peak times, top hosts &amp; sites). Uncheck to opt out.
                </span>
              </span>
            </label>
          </div>

          {/* Phase C1.5 — Module 7 Returning Visitor fast lane toggle. */}
          <div className="border-t border-navy-100 pt-4">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="returning-visitor-toggle-row">
              <input
                type="checkbox"
                checked={org.returning_visitor_enabled !== false}
                onChange={(e) => setOrg({ ...org, returning_visitor_enabled: e.target.checked })}
                className="mt-1 h-4 w-4 accent-accent"
                data-testid="returning-visitor-toggle"
              />
              <span className="flex-1">
                <span className="flex items-center gap-2 eb-label !mb-0">
                  <Sparkles className="w-4 h-4 text-accent" />
                  Returning visitor fast lane
                </span>
                <span className="block text-xs text-navy-500 mt-1">
                  Show an "I've been here before" tile on the kiosk. Returning visitors
                  can sign in with one tap; photo &amp; long form are skipped.
                  Consent re-acceptance is required if any policy has changed since
                  their last visit.
                </span>
              </span>
            </label>
            {/* Phase C1.6 — per-org time-saved estimate. Only relevant when
                the lane is enabled; nudge that to the user via opacity. */}
            <div className={`mt-3 ml-7 ${org.returning_visitor_enabled === false ? "opacity-50" : ""}`}
                 data-testid="returning-visitor-seconds-row">
              <label className="eb-label !mb-1 text-xs" htmlFor="rv-seconds">
                Estimated seconds saved per fast-lane sign-in
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="rv-seconds"
                  type="number"
                  min={30}
                  max={180}
                  step={5}
                  value={org.returning_visitor_estimated_seconds_saved ?? 60}
                  onChange={(e) => setOrg({
                    ...org,
                    returning_visitor_estimated_seconds_saved: e.target.value === "" ? 60 : Number(e.target.value),
                  })}
                  className="eb-input w-24"
                  data-testid="returning-visitor-seconds-input"
                  disabled={org.returning_visitor_enabled === false}
                />
                <span className="text-xs text-navy-500">seconds (30–180, default 60)</span>
              </div>
              <p className="text-[11px] text-navy-400 mt-1 leading-snug">
                Drives the "≈ X minutes saved" figure on the Live Dashboard ROI card.
                Lower it if your standard form is unusually short, raise it for sites
                with photo capture + RAMS acceptance.
              </p>
            </div>
          </div>
          <div>
            <label className="eb-label">Branding (placeholder)</label>
            <div className="text-sm text-navy-500 p-3 rounded-lg bg-surface border border-navy-100">
              Logo upload + colour overrides coming in Phase 5. Kiosk welcome text will live here.
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" disabled={saving} className="btn-accent" data-testid="save-settings-btn">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </form>

        <div className="eb-card p-6 mt-6 border-warning/40" data-testid="danger-zone">
          <div className="flex items-center gap-2 mb-3">
            <SettingsIcon className="w-4 h-4 text-warning" />
            <h2 className="font-semibold">Demo data controls</h2>
          </div>
          <p className="text-sm text-navy-600 mb-4">Wipe and re-seed the canonical demo data (one site, three hosts, a sample contractor with RAMS PDF, and a permit-to-work).</p>
          <button onClick={resetDemo} className="btn-outline" data-testid="reset-demo-btn">
            <Database className="w-4 h-4" /> Reset demo data
          </button>
        </div>

        {/* Phase B2 — Locale & region card. Reads/writes org locale through
            the same /orgs/me PATCH the form above uses but with a dedicated
            UI surface (live preview, separate save action) so users discover
            the setting and see its effect immediately. */}
        <div className="mt-6">
          <LocaleCard
            scope="org"
            initialLocale={org.locale || "en-GB"}
            onSaved={(newLocale) => setOrg((o) => ({ ...o, locale: newLocale }))}
          />
        </div>
      </div>
    </SettingsLayout>
  );
}
